"use client";

import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

import {
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { useValueFormat } from "../../hooks/use-value-format";
import { useWidgetAction } from "../../hooks/use-widget-action";
import { WidgetOpenButton, WidgetOpenLink } from "../widget-open-link";

import type { StatItem, StatPayload, Tone } from "@/types";

// The delta pill: text and its own tinted ground, from one tone.
//
// Mint for a rising figure rather than the green the app uses elsewhere —
// --foreground-mint is the accent that survives both themes, and on a dark tile
// a saturated green at 12px goes muddy where the mint stays legible. Neutral
// stays muted so a "+0" doesn't shout.
const DELTA_TONES: Record<Tone, string> = {
  positive: "bg-foreground-mint/12 text-foreground-mint",
  negative: "bg-tx-red/12 text-tx-red",
  neutral: "bg-fg-muted/12 text-fg-muted",
};

// Red on the FIGURE is reserved for a falling trend — a tile whose number is
// red at rest would spend the colour on nothing and leave it meaningless when
// something is actually wrong. Every other state keeps the ordinary foreground.
const VALUE_TONES: Record<Tone, string> = {
  positive: "text-foreground",
  negative: "text-tx-red",
  neutral: "text-foreground",
};

/**
 * Headline numbers — the count tiles in the dashboard's header strip.
 *
 * Label and ↗ chip on one row, big tabular number below, then the second figure.
 *
 * There was a hairline above that second figure, added because the secondary line
 * otherwise floated with nothing tying it to anything. The design system settles
 * the question differently — it draws no rules inside a card and separates a
 * footer by space alone — so the rule is gone and the spacing carries it.
 *
 * There was briefly a proportion bar here. It was dropped: a meter only earns its
 * place when the part genuinely belongs to the whole AND someone would act on the
 * ratio. "9 active of 10 orders" is a fact you read once, not a gauge you watch,
 * and on the earnings tile the whole was lifetime pay — a bar that shrinks forever
 * and can never be acted on.
 *
 * It renders the Card's INNER slots rather than a Card of its own, because the
 * compact WidgetCard already supplies the Card (and its loading / error states).
 *
 * Knows nothing about users, orders or earnings: it renders whatever figures the
 * backend sent, in the declared format, so a currency stat lands in the company's
 * own currency.
 */
export function StatWidget({ payload }: { payload: StatPayload }) {
  return (
    <>
      {payload.items.map((item) => (
        <Stat key={item.label} item={item} />
      ))}
    </>
  );
}

function Stat({ item }: { item: StatItem }) {
  const format = useValueFormat();
  const runAction = useWidgetAction();
  const delta = item.delta;

  // One control, two destinations. Bound to a local so it stays narrowed inside
  // the handler — TypeScript drops property narrowing across a closure.
  const action = item.action;

  // Direction comes from the number; COLOUR comes from the backend's tone,
  // because rising is not always good (new claims, say).
  const TrendIcon =
    delta === null || delta.value === 0
      ? Minus
      : delta.value > 0
        ? ArrowUpRight
        : ArrowDownRight;

  // A trend's sign is already carried by the arrow and the colour, so the pill
  // shows the magnitude — "↘ 4.87%", not "↘ -4.87%", which says it twice and
  // reads as a double negative. A state count keeps whatever it sent.
  const deltaValue =
    delta && delta.trend ? Math.abs(delta.value) : (delta?.value ?? 0);

  // The figure only takes the trend's colour when there IS a trend: on a state
  // count ("9 active") a red number would announce a problem nobody reported.
  const valueTone = delta?.trend ? VALUE_TONES[delta.tone] : "text-foreground";

  return (
    <>
      {/* gap-0 because the figure sets its own 18px lead — the grid's default
          4px gap would fight it and leave the number crowding its label. */}
      <CardHeader className="relative gap-0">
        {/* 15px and near-foreground, not the muted 14px a card description
            defaults to: on a count tile the label names the figure and is read
            WITH it, where a muted caption reads as an aside.

            It also follows the figure's colour, as the reference design has it —
            on a falling tile the whole statement is one red thing rather than a
            red number under a grey word. */}
        <CardDescription
          className={cn(
            "text-[0.9375rem] leading-tight text-foreground/85",
            delta?.trend && VALUE_TONES[delta.tone],
          )}
        >
          {item.label}
        </CardDescription>

        {/* Top-right of the tile, exactly where the DS's StatCard puts it.
            CardAction is the shadcn slot for that corner, so the chip sits in the
            header grid rather than being absolutely positioned over it. The
            figure's own label names the link, because a multi-item stat payload
            renders several tiles and "view all" would be ambiguous. */}
        <CardAction>
          {action ? (
            <WidgetOpenButton
              onClick={() => runAction(action)}
              label={item.label}
            />
          ) : (
            <WidgetOpenLink href={item.href} label={item.label} />
          )}
        </CardAction>

        {/* The figure is the point of the tile, so it dominates.
            REGULAR weight and line-height 0.9 are the DS's `.buro-money` spec
            exactly — the design system makes its numbers large and LIGHT rather
            than heavy, and the tight leading keeps a big figure sitting close to
            its label.
            The SIZE is scaled rather than copied. The DS specifies 24px, but for
            a StatCard about 166px wide on a 375px device; the CRM's header tile
            is roughly twice that, and at 24px the number stopped reading as the
            headline. 32px holds the reference tile's proportion at this width,
            and the 4-column desktop strip — where tiles are widest — steps up to
            the 38px the design is drawn at. */}
        <CardTitle
          className={cn(
            // The `!` is load-bearing. CardTitle carries shadcn's
            // `group-data-[size=sm]/card:text-sm`, which compiles to a 0-2-0
            // selector (`.class:is([data-size=sm] *)`) where a plain utility is
            // 0-1-0 — so the card's own rule won on specificity and the figure
            // rendered at 14px however large a size was passed here. It is not a
            // conflict tailwind-merge can see either: different modifiers, so
            // both classes survive into the DOM and the cascade decides.
            "mt-[18px] font-display text-[2rem]! leading-[0.9] font-normal tracking-tight tabular-nums lg:text-[2.375rem]!",
            valueTone,
          )}
        >
          {format(item.value, item.format)}
          {item.value_suffix && (
            // Smaller and muted, not the number's own size: it names the figure
            // rather than being part of it, and at 24px the word would compete
            // with the amount for the eye. `tabular-nums` stays on the parent so
            // the digits still align across tiles.
            //
            // font-sans explicitly, because this span sits INSIDE CardTitle and
            // would otherwise inherit its font-display (Montserrat) — the face
            // this app reserves for figures and titles. It then rendered in a
            // different typeface from the delta's caption in the footer, which is
            // the same kind of word doing the same job one line below.
            <span className="ml-1.5 align-baseline font-sans text-sm font-normal text-fg-muted lg:text-base">
              {item.value_suffix}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      {delta && (
        // No rule above the second figure: the DS separates a card's footer by
        // space alone (StatCard gives it marginTop 12 and nothing else), and the
        // hairline was part of the banded-card language we've dropped. No top
        // padding either — the Card's own 16px gap already does that job, and
        // 12px on top of it pushed the pill away from the number it belongs to.
        <CardFooter className="relative items-center gap-2 pt-0">
          {/*
            A change over time gets the pill — arrow, figure and its own tinted
            ground, the chip shape the DS uses for every delta. A state count
            ("9 active") gets none of it: an arrow there reads as though a
            growing backlog were an achievement, and a coloured chip would give
            a standing number the weight of news.
          */}
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-[9px] py-1 text-xs font-medium tabular-nums",
              delta.trend ? DELTA_TONES[delta.tone] : "text-fg-muted",
            )}
          >
            {delta.trend && <TrendIcon className="size-[13px]" />}
            {format(deltaValue, delta.format)}
          </span>
          {/* 13px, a step below the label and a step above the pill: it
              qualifies the percentage rather than competing with it. */}
          {delta.caption && (
            <span className="truncate text-[0.8125rem] text-fg-muted">
              {delta.caption}
            </span>
          )}
        </CardFooter>
      )}
    </>
  );
}
